import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { notificationService } from '../../services/notificationService';
import { InAppNotification, NotificationType } from '../../types';
import { Link } from '../../router/Router';
import {
  Bell,
  CheckCircle2,
  XCircle,
  Info,
  Calendar,
  CheckCheck,
  Phone,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';

export const NotificationCenter: React.FC = () => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<'ALL' | 'UNREAD'>('ALL');
  const [isMarkingAll, setIsMarkingAll] = useState(false);

  useEffect(() => {
    if (!user) return;
    setIsLoading(true);

    const unsubscribe = notificationService.subscribeToNotifications(
      user.uid,
      (items) => {
        setNotifications(items);
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user?.uid]);

  const handleMarkAsRead = async (id: string, read: boolean) => {
    if (read) return;
    await notificationService.markAsRead(id);
  };

  const handleMarkAllAsRead = async () => {
    if (isMarkingAll || unreadCount === 0) return;
    setIsMarkingAll(true);
    await notificationService.markAllAsRead();
    setIsMarkingAll(false);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const filteredNotifications = notifications.filter((item) => {
    if (activeFilter === 'UNREAD') {
      return !item.read;
    }
    return true;
  });

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case 'BOOKING_CONFIRMED':
      case 'PAYMENT_SUCCESS':
      case 'PAYMENT_APPROVED':
        return <CheckCircle2 className="notif-type-icon notif-icon-success" size={24} />;
      case 'BOOKING_CANCELLED':
      case 'PAYMENT_FAILED':
      case 'PAYMENT_REJECTED':
        return <XCircle className="notif-type-icon notif-icon-danger" size={24} />;
      case 'TRIP_REMINDER':
      case 'TRIP_UPDATED':
      case 'TRIP_CANCELLED':
        return <Calendar className="notif-type-icon notif-icon-info" size={24} />;
      case 'SYSTEM':
      default:
        return <Info className="notif-type-icon notif-icon-default" size={24} />;
    }
  };

  const formatNotificationTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('ar-EG', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  return (
    <div className="notification-center-container" dir="rtl">
      {/* Header & Breadcrumbs */}
      <div className="notif-breadcrumbs">
        <Link to="/student" className="breadcrumb-link">
          لوحة الطالب
        </Link>
        <span className="breadcrumb-sep">/</span>
        <span className="breadcrumb-current">مركز الإشعارات</span>
      </div>

      <div className="notif-header-card">
        <div className="notif-title-row">
          <div className="title-with-badge">
            <Bell className="header-bell-icon" size={28} />
            <h1>إشعاراتي</h1>
            {unreadCount > 0 && (
              <span className="unread-counter-badge">{unreadCount} جديد</span>
            )}
          </div>

          {unreadCount > 0 && (
            <button
              className="btn-mark-all-read"
              onClick={handleMarkAllAsRead}
              disabled={isMarkingAll}
            >
              <CheckCheck size={18} />
              {isMarkingAll ? 'جاري التحديد...' : 'تحديد الكل كمقروء'}
            </button>
          )}
        </div>

        {/* Filter Tabs */}
        <div className="notif-filter-tabs">
          <button
            className={`filter-tab ${activeFilter === 'ALL' ? 'active' : ''}`}
            onClick={() => setActiveFilter('ALL')}
          >
            الكل ({notifications.length})
          </button>
          <button
            className={`filter-tab ${activeFilter === 'UNREAD' ? 'active' : ''}`}
            onClick={() => setActiveFilter('UNREAD')}
          >
            غير المقروءة ({unreadCount})
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="notif-list-container">
        {isLoading ? (
          <div className="loading-card">
            <div className="loading-spinner"></div>
            <p>جاري جلب إشعاراتك...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="empty-notif-card">
            <Bell size={48} className="empty-bell-icon" />
            <h3>لا توجد إشعارات {activeFilter === 'UNREAD' ? 'غير مقروءة' : 'حالياً'}</h3>
            <p>سنوافيك فوراً بآخر التحديثات فور تأكيد حجوزاتك أو عمليات الدفع.</p>
          </div>
        ) : (
          <div className="notifications-feed">
            {filteredNotifications.map((notif) => (
              <div
                key={notif.id}
                className={`notif-card-item ${notif.read ? 'read' : 'unread'}`}
                onClick={() => handleMarkAsRead(notif.id, notif.read)}
              >
                <div className="notif-icon-col">
                  {getNotificationIcon(notif.type)}
                </div>

                <div className="notif-content-col">
                  <div className="notif-item-header">
                    <h3 className="notif-item-title">{notif.title}</h3>
                    <span className="notif-item-time">
                      {formatNotificationTime(notif.createdAt)}
                    </span>
                  </div>

                  <p className="notif-item-body">{notif.body || (notif as any).message}</p>

                  {/* Context Action Links */}
                  {notif.type === 'PAYMENT_REJECTED' ? (
                    (() => {
                      const targetResubmitId =
                        (notif.data as any)?.bookingGroupId ||
                        notif.bookingGroupId ||
                        (notif.data as any)?.bookingId ||
                        notif.bookingId;
                      const targetPaymentUrl =
                        notif.actionUrl ||
                        (notif.data as any)?.targetPaymentUrl ||
                        (targetResubmitId ? `/student/payment/${targetResubmitId}` : '/student/bookings');
                      const reasonText =
                        (notif.data as any)?.rejectionReason ||
                        notif.rejectionReason ||
                        (notif.data as any)?.adminNote;

                      return (
                        <div style={{ marginTop: '0.65rem' }}>
                          {reasonText && (
                            <div
                              style={{
                                background: '#FFF1F2',
                                border: '1px solid #FECDD3',
                                borderRadius: '10px',
                                padding: '0.45rem 0.75rem',
                                marginBottom: '0.65rem',
                                fontSize: '0.82rem',
                                color: '#9F1239',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.45rem',
                              }}
                            >
                              <AlertTriangle size={15} color="#E11D48" />
                              <span>
                                <strong>سبب الرفض:</strong> {String(reasonText)}
                              </span>
                            </div>
                          )}

                          <div className="notif-actions-row" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <Link
                              to={targetPaymentUrl}
                              className="notif-action-btn"
                              style={{
                                background: 'linear-gradient(135deg, #E5232A 0%, #B91C1C 100%)',
                                color: 'white',
                                fontWeight: 800,
                                padding: '0.55rem 1.15rem',
                                borderRadius: '10px',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '0.45rem',
                                boxShadow: '0 4px 12px rgba(229, 35, 42, 0.25)',
                                textDecoration: 'none',
                                border: 'none',
                                fontSize: '0.85rem',
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notif.id, notif.read);
                              }}
                            >
                              <RefreshCw size={14} />
                              <span>تعديل بيانات الدفع والإثبات وإعادة الإرسال ↺</span>
                            </Link>

                            <Link
                              to="/student/bookings"
                              className="notif-action-btn"
                              style={{ fontSize: '0.82rem' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMarkAsRead(notif.id, notif.read);
                              }}
                            >
                              متابعة في قسم رحلاتي
                            </Link>

                            <a
                              href="tel:01207337330"
                              className="notif-action-btn"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontSize: '0.82rem' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Phone size={13} />
                              <span>اتصال بالدعم</span>
                            </a>

                            <a
                              href={`https://wa.me/201207337330?text=${encodeURIComponent(`مرحباً خدمة العملاء، استفسار بخصوص إشعار رفض الحجز رقم ${(notif.data as any)?.bookingCode || ''}`)}`}
                              target="_blank"
                              rel="noreferrer"
                              className="notif-action-btn"
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none', color: '#16A34A', borderColor: '#BBF7D0', fontSize: '0.82rem' }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <span>واتساب الدعم</span>
                            </a>
                          </div>
                        </div>
                      );
                    })()
                  ) : notif.data?.bookingId ? (
                    <div className="notif-actions-row">
                      <Link
                        to={`/student/booking/${notif.data.bookingId}`}
                        className="notif-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notif.id, notif.read);
                        }}
                      >
                        عرض التذكرة والحجز
                      </Link>
                    </div>
                  ) : notif.type === 'PAYMENT_APPROVED' ? (
                    <div className="notif-actions-row">
                      <Link
                        to="/student/bookings"
                        className="notif-action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMarkAsRead(notif.id, notif.read);
                        }}
                      >
                        عرض التذاكر في رحلاتي
                      </Link>
                    </div>
                  ) : null}
                </div>

                {!notif.read && <span className="unread-dot" title="غير مقروء"></span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
